import { useState } from "react";
import { Button } from "@/components/ui";
import type { WorkToastQuestionOption } from "@/stores/work-toast-store";

/** Shared option chips for the work toast and compact cursor question. */
export function WorkToastQuestionOptions({
  options,
  disabled,
  onPickOption,
  testIdPrefix = "work-toast-option",
}: {
  options: WorkToastQuestionOption[];
  disabled: boolean;
  onPickOption: (option: WorkToastQuestionOption) => void;
  testIdPrefix?: string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (options.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {options.slice(0, 9).map((option, index) => (
        <Button
          key={option.id}
          type="button"
          disabled={disabled}
          variant="secondary"
          onPointerEnter={() => setExpandedId(option.id)}
          onPointerLeave={() => setExpandedId(null)}
          onFocus={() => setExpandedId(option.id)}
          onBlur={() => setExpandedId(null)}
          onClick={() => onPickOption(option)}
          className="h-auto min-h-11 justify-start gap-2.5 whitespace-normal rounded-[16px] px-3 py-1.5 text-left text-appkit-body"
          aria-describedby={
            option.task?.description && expandedId === option.id
              ? `${testIdPrefix}-${option.id}-details`
              : undefined
          }
          data-testid={`${testIdPrefix}-${option.id}`}
          data-interactive="true"
        >
          <kbd className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border border-glass-stroke-subtle text-appkit-mini font-semibold text-muted">
            {index + 1}
          </kbd>
          <span className="min-w-0">
            {option.label}
            {option.description && (
              <span className="block text-appkit-small font-medium text-muted">
                {option.description}
              </span>
            )}
            {option.task?.description && expandedId === option.id && (
              <span
                id={`${testIdPrefix}-${option.id}-details`}
                data-testid={`${testIdPrefix}-${option.id}-details`}
                className="mt-1 block text-appkit-small font-medium text-muted"
              >
                {option.task.description}
              </span>
            )}
          </span>
        </Button>
      ))}
    </div>
  );
}
